import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';
import { DialogRef } from '@angular/cdk/dialog';
import { SearchFilterComponent } from '../search-filter/search-filter.component';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { SearchResult } from '../../models/search-result.model';
import { SearchService } from '../../services/search.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SearchResultComponent } from '../search-result/search-result.component';
import { Router } from '@angular/router';
import { SearchFilter } from '../../models/search-filter.model';
import { SearchSortOption } from '../../models/search-sort-option.model';
import {
  EnvironmentModel,
  ErrorComponent,
  LoadingComponent,
  mapToRequestState,
  OrderBy,
  RequestState,
} from '@prime-platform/shared';

@Component({
  selector: 'prime-search',
  standalone: true,
  imports: [
    CommonModule,
    SearchFilterComponent,
    ReactiveFormsModule,
    SearchResultComponent,
    FormsModule,
    LoadingComponent,
    ErrorComponent,
  ],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SearchComponent implements OnInit, OnDestroy {
  allFilters: SearchFilter[] = [
    { label: 'statements', value: 'statement', selected: false },
    { label: 'references', value: 'reference', selected: false },
    { label: 'resources', value: 'resource', selected: false },
    { label: 'congresses', value: 'congress', selected: false },
  ];
  filters: SearchFilter[] = [...this.allFilters];
  sortOptions: SearchSortOption[] = [
    { label: 'Most relevant', value: 'matchCount', order: OrderBy.desc },
    {
      label: 'Recently published',
      value: 'publishedDate',
      order: OrderBy.desc,
    },
  ];
  results$!: Observable<RequestState<SearchResult[]>>;
  searchTerm = new FormControl('', { nonNullable: true });
  selectedFilters: string[] = [];
  selectedSortOption!: SearchSortOption;
  sub = new Subscription();
  displayAllSearchFilters: boolean;

  constructor(
    public dialogRef: DialogRef<string>,
    private searchService: SearchService,
    private router: Router,
    private location: Location,
    @Inject('environment') private environment: EnvironmentModel
  ) {
    this.displayAllSearchFilters = this.environment.displayAllSearchFilters ?? true;
  }

  ngOnInit() {
    this.sub.add(
      this.searchTerm.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(() => this.search())
        )
        .subscribe()
    );
    this.selectedSortOption = this.sortOptions[0];
  }

  updateSelectedFilters(filters: string[]) {
    this.selectedFilters = [...filters];
    this.search();
  }

  search(): Observable<RequestState<SearchResult[]>> {
    this.results$ = mapToRequestState(
      this.searchService.search(
        this.searchTerm.value,
        this.selectedFilters,
        this.selectedSortOption
      ).pipe(
        tap((results) => {
          if (!this.displayAllSearchFilters && results.length > 0) {
            this.updateVisibleFilters(results);
          } else if (!this.displayAllSearchFilters && results.length === 0) {
            this.filters = [...this.allFilters];
          }
        })
      )
    );
    return this.results$;
  }

  private updateVisibleFilters(results: SearchResult[]): void {
    const typesWithResults = new Set(results.map((result) => result.type));
    this.filters = this.allFilters
      .filter((filter) => typesWithResults.has(filter.value))
      .map((filter) => {
        const currentFilter = this.filters.find((f) => f.value === filter.value);
        return {
          ...filter,
          selected: currentFilter ? currentFilter.selected : false,
        };
      });
  }

  goToResult(result: SearchResult) {
    if (result.type === 'resource' || result.type === 'reference') {
      this.router.navigate([
        '',
        { outlets: { modal: ['viewer', result.type, result.id] } },
      ]);
          } else {
        const route = result.breadCrumbs[0].label.toLowerCase() +
          result.breadCrumbs[result.breadCrumbs.length - 1].route;
        const fullUrl = window.location.origin + this.location.prepareExternalUrl(route);
        window.open(fullUrl, '_blank');
      }
  }

  goToSection(route: string) {
    const fullUrl = window.location.origin + this.location.prepareExternalUrl(route);
    window.open(fullUrl, '_blank');
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
